import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { PageHeader } from "./OwnerPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, ChevronRight, Store, Truck, Receipt, IndianRupee, MapPin, Printer, FileText, Trash2, Edit } from "lucide-react";
import LocationDocumentsBlock from "./LocationDocumentsBlock";
import { DocumentsGallery } from "./OwnerPages";

const OK_STATUS = ["invoiced", "dispatched", "delivered", "completed", "fulfilled"];
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const lineTotal = (l) => {
  const bq = Math.max(Number(l.box_qty) || 1, 1);
  const bp = Number(l.box_price) || 0;
  return bp * (Number(l.boxes) || 0) + (bp / bq) * (Number(l.pcs) || 0);
};

export default function OwnerEditOrder({ order, onDone }) {
  const [allowed, setAllowed] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [lines, setLines] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [browseOpen, setBrowseOpen] = React.useState(false);
  const [products, setProducts] = React.useState([]);
  const [cat, setCat] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [qtyMap, setQtyMap] = React.useState({});
  const [lotsByPid, setLotsByPid] = React.useState({});
  const [manualBatch, setManualBatch] = React.useState({});

  const statusOk = !!order && OK_STATUS.includes(order.status);
  React.useEffect(() => {
    if (!statusOk) return undefined;
    let live = true;
    dms.ownerEditAllowed().then(() => { if (live) setAllowed(true); }).catch(() => { if (live) setAllowed(false); });
    return () => { live = false; };
  }, [statusOk]);

  if (!statusOk || !allowed) return null;

  const openEditor = () => {
    const rows = (order.items || [])
      .filter(it => Number(it.qty_boxes_invoiced ?? it.qty_boxes_ordered ?? 0) > 0 || Number(it.qty_pcs_invoiced ?? it.qty_pcs_ordered ?? 0) > 0)
      .map(it => ({
        product_id: it.product_id, product_name: it.product_name, sku_code: it.sku_code,
        box_qty: it.box_qty || 1, box_price: it.box_price || 0,
        boxes: String(it.qty_boxes_invoiced ?? it.qty_boxes_ordered ?? 0),
        pcs: String(it.qty_pcs_invoiced ?? it.qty_pcs_ordered ?? 0),
        priceEdited: false, isNew: false,
      }));
    setLines(rows);
    setManualBatch({});
    setOpen(true);
    loadLots(rows.map(r => r.product_id));
  };

  const setLine = (i, patch) => setLines(prev => prev.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines(prev => prev.filter((_, k) => k !== i));

  const wasDispatched = !!(order.challan_id || order.dispatched_at);
  const loadLots = async (pids) => {
    const out = {};
    await Promise.all(pids.map(async id => {
      try {
        const r = await dms.distributorStockLots(id, order.distributor_id);
        out[id] = r.data || [];
      } catch (e) { out[id] = []; }
    }));
    setLotsByPid(prev => ({ ...prev, ...out }));
  };
  const pcsOf = (l) => Math.round((Number(l.boxes) || 0) * (Number(l.box_qty) || 1)) + (order.mode === "box_pcs" ? (Number(l.pcs) || 0) : 0);
  const oldAllocs = (l) => ((order.items || []).find(it => it.product_id === l.product_id) || {}).allocations || [];
  const lotAvail = (l, lot) => {
    const bq = Number(l.box_qty) || 1;
    let a = Math.floor(Number(lot.qty_boxes || 0) * bq + 1e-6);
    if (wasDispatched) {
      const o = oldAllocs(l).find(x => Number(x.batch_no) === Number(lot.batch_no));
      if (o) a += Number(o.qty_pcs || 0);
    }
    return a;
  };
  const fifoPreview = (l) => {
    const lots = lotsByPid[l.product_id] || [];
    let need = pcsOf(l);
    const out = [];
    for (const lot of lots) {
      const avail = lotAvail(l, lot);
      if (avail <= 0 || need <= 0) continue;
      const take = Math.min(avail, need);
      out.push({ batch_no: lot.batch_no, qty_pcs: take });
      need -= take;
    }
    if (need > 0 && lots.length) {
      const last = lots[lots.length - 1].batch_no;
      if (out.length && out[out.length - 1].batch_no === last) out[out.length - 1].qty_pcs += need;
      else out.push({ batch_no: last, qty_pcs: need });
    }
    return out;
  };
  const currentOrFifo = (l) => {
    const oa = oldAllocs(l);
    if (oa.length && oa.reduce((s, x) => s + Number(x.qty_pcs || 0), 0) === pcsOf(l)) {
      return oa.map(x => ({ batch_no: x.batch_no, qty_pcs: Number(x.qty_pcs || 0) }));
    }
    return fifoPreview(l);
  };
  const manualAllocs = (l) => {
    const m = manualBatch[l.product_id];
    if (!m) return null;
    return Object.entries(m).map(([bn, q]) => ({ batch_no: Number(bn), qty_pcs: Number(q || 0) })).filter(x => x.qty_pcs > 0);
  };
  const toggleManual = (l) => {
    setManualBatch(prev => {
      const n = { ...prev };
      if (n[l.product_id]) { delete n[l.product_id]; return n; }
      const init = {};
      currentOrFifo(l).forEach(x => { init[x.batch_no] = String(x.qty_pcs); });
      n[l.product_id] = init;
      return n;
    });
  };
  const batchPicker = (l) => {
    const lots = lotsByPid[l.product_id] || [];
    if (lots.length < 2) return null;
    const manual = manualBatch[l.product_id];
    const total = pcsOf(l);
    const sum = manual ? Object.values(manual).reduce((s, q) => s + Number(q || 0), 0) : 0;
    return (
      <div className="mt-2 text-xs" data-testid={`edit-batch-picker-${l.product_id}`}>
        {!manual ? (
          <div className="flex items-center gap-2 flex-wrap text-slate-600">
            <span>Batch:</span>
            {currentOrFifo(l).map(x => <span key={x.batch_no} className="px-1.5 py-0.5 rounded bg-slate-100">B{x.batch_no} · {x.qty_pcs} pcs</span>)}
            <button type="button" onClick={() => toggleManual(l)} className="text-[#a67c00] underline">Choose batch</button>
          </div>
        ) : (
          <div className="space-y-1">
            {lots.map(lot => (
              <div key={lot.batch_no} className="flex items-center gap-2">
                <span className="w-48 text-slate-600">{lot.label || `Batch ${lot.batch_no}`} <span className="text-slate-400">({lotAvail(l, lot)} pcs available)</span></span>
                <input type="number" min={0} value={manual[lot.batch_no] ?? ""}
                  onChange={e => setManualBatch(prev => ({ ...prev, [l.product_id]: { ...prev[l.product_id], [lot.batch_no]: e.target.value } }))}
                  className="w-20 h-8 text-center border border-slate-200 rounded-lg" />
                <span>pcs</span>
              </div>
            ))}
            <div className={sum === total ? "text-emerald-600" : "text-rose-600"}>Split {sum} / {total} pcs</div>
            <button type="button" onClick={() => toggleManual(l)} className="text-slate-500 underline">Back to auto</button>
          </div>
        )}
      </div>
    );
  };

  const openBrowse = async () => {
    setCat(null); setSearch(""); setQtyMap({});
    try {
      const b = await dms.retailerBrowse(order.retailer_id);
      const list = b.data || b.products || [];
      const have = new Set(lines.map(l => l.product_id));
      setProducts(list.filter(p => !have.has(p.id || p.product_id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load products");
      setProducts([]);
    }
    setBrowseOpen(true);
  };

  const pid = (p) => p.id || p.product_id;
  const catOf = (p) => p.category_name || p.category || "Uncategorised";
  const grouped = {};
  products.forEach(p => { (grouped[catOf(p)] ||= []).push(p); });
  const catList = Object.entries(grouped).map(([name, list]) => ({
    name, count: list.length, picked: list.filter(p => (qtyMap[pid(p)] || 0) > 0).length,
  }));
  const q = search.trim().toLowerCase();
  const visible = q
    ? products.filter(p => `${p.name || p.product_name || ""} ${p.sku_code || ""}`.toLowerCase().includes(q))
    : (cat ? (grouped[cat] || []) : []);
  const bump = (id, d) => setQtyMap(prev => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) + d) }));

  const addSelected = () => {
    const add = products
      .filter(p => (qtyMap[pid(p)] || 0) > 0)
      .map(p => ({
        product_id: pid(p), product_name: p.name || p.product_name, sku_code: p.sku_code,
        box_qty: p.box_qty || 1, box_price: Number(p.box_price ?? p.selling_price ?? 0),
        boxes: String(qtyMap[pid(p)]), pcs: "0", priceEdited: false, isNew: true,
      }));
    if (!add.length) { toast.error("Select at least one product"); return; }
    setLines(prev => [...prev, ...add]);
    loadLots(add.map(a => a.product_id));
    setBrowseOpen(false);
  };

  const newTotal = lines.reduce((s, l) => s + lineTotal(l), 0);

  const save = async () => {
    const valid = lines.filter(l => (Number(l.boxes) || 0) > 0 || (Number(l.pcs) || 0) > 0);
    if (!valid.length) { toast.error("At least one item with quantity is required"); return; }
    const _n = (v) => Number(v) || 0;
    const _oq = (it, k) => _n(it["qty_" + k + "_invoiced"] ?? it["qty_" + k + "_ordered"]);
    const origLines = (order.items || []).filter(it => _oq(it, "boxes") > 0 || _oq(it, "pcs") > 0);
    const validIds = new Set(valid.map(l => l.product_id));
    const origIds = new Set(origLines.map(it => it.product_id));
    const removed = origLines.filter(it => !validIds.has(it.product_id));
    const added = valid.filter(l => !origIds.has(l.product_id));
    const changed = [];
    origLines.forEach(it => {
      const l = valid.find(x => x.product_id === it.product_id);
      if (!l) return;
      const priceChg = l.priceEdited && _n(l.box_price) !== _n(it.box_price);
      if (_oq(it, "boxes") !== _n(l.boxes) || _oq(it, "pcs") !== _n(l.pcs) || priceChg) {
        changed.push(`${it.product_name}: ${_oq(it, "boxes")} box + ${_oq(it, "pcs")} pcs -> ${_n(l.boxes)} box + ${_n(l.pcs)} pcs${priceChg ? ` (box price ${_n(it.box_price)} -> ${_n(l.box_price)})` : ""}`);
      }
    });
    valid.forEach(l => {
      const al = manualAllocs(l);
      if (!al || !origIds.has(l.product_id)) return;
      const oa = oldAllocs(l).map(x => Number(x.batch_no) + ":" + Number(x.qty_pcs)).sort().join(",");
      const na = al.map(x => Number(x.batch_no) + ":" + Number(x.qty_pcs)).sort().join(",");
      if (oa !== na) changed.push(`${l.product_name}: batch split -> ` + al.map(x => `B${x.batch_no} ${x.qty_pcs} pcs`).join(", "));
    });
    if (!removed.length && !changed.length && !added.length) { toast.info("No changes to save"); return; }
    const pendingKept = (order.items || []).filter(it => !origIds.has(it.product_id));
    const parts = [`Save changes to ${order.order_no}?`];
    if (removed.length) parts.push("REMOVED:\n" + removed.map(it => "  - " + it.product_name).join("\n"));
    if (changed.length) parts.push("CHANGED:\n" + changed.map(c => "  - " + c).join("\n"));
    if (added.length) parts.push("ADDED:\n" + added.map(l => `  - ${l.product_name}: ${_n(l.boxes)} box + ${_n(l.pcs)} pcs`).join("\n"));
    if (pendingKept.length) parts.push(`Pending lines (not invoiced) stay unchanged: ${pendingKept.length}`);
    parts.push(`Invoice ${order.invoice_no || ""} and challan ${order.challan_no || ""} numbers stay the same.\nStock, invoice, ledger and challan will be updated.`);
    for (const l of valid) {
      const al = manualAllocs(l);
      if (al) {
        const s = al.reduce((t, x) => t + x.qty_pcs, 0);
        if (s !== pcsOf(l)) { toast.error(`${l.product_name}: batch split (${s} pcs) must equal quantity (${pcsOf(l)} pcs)`); return; }
      }
    }
    if (!window.confirm(parts.join("\n\n"))) return;
    setBusy(true);
    try {
      const items = valid.map(l => ({
        product_id: l.product_id,
        qty_boxes: Number(l.boxes) || 0,
        qty_pcs: Number(l.pcs) || 0,
        ...(l.priceEdited ? { box_price: Number(l.box_price) } : {}),
        ...(manualAllocs(l) ? { allocations: manualAllocs(l) } : {}),
      }));
      const r = await dms.ownerEditOrder(order.id, { items, removed_product_ids: removed.map(it => it.product_id) });
      const _sba = (r.stock_before_after || []).map(s => `${s.product_name}: ${s.before} -> ${s.after}`).join(" | ");
      toast.success(`Order updated: ${money(r.old_total)} -> ${money(r.total)}`, _sba ? { description: `Stock (boxes) ${_sba}`, duration: 12000 } : undefined);
      setOpen(false);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" onClick={openEditor} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="owner-edit-order-btn">
        Edit Invoice
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-lg font-semibold text-slate-900">Edit {order.order_no}</div>
                <div className="text-xs text-slate-500">Invoice {order.invoice_no || "-"} / Challan {order.challan_no || "-"} numbers will not change. Stock, invoice, ledger and challan update automatically.</div>
              </div>
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                  <tr>
                    <th className="text-left p-2">Product</th>
                    <th className="p-2">Box price (incl. GST)</th>
                    <th className="p-2">Boxes</th>
                    <th className="p-2">Pcs</th>
                    <th className="text-right p-2">Total</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 && (<tr><td colSpan={6} className="p-4 text-center text-slate-400">No items - add one</td></tr>)}
                  {lines.map((l, i) => (
                    <tr key={l.product_id} className="border-t border-slate-100">
                      <td className="p-2">
                        <div className="font-medium text-slate-900">{l.product_name}{l.isNew && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">NEW</span>}</div>
                        <div className="text-[11px] font-mono text-slate-500">{l.sku_code} - {l.box_qty} pcs/box</div>
                        {batchPicker(l)}
                      </td>
                      <td className="p-2 text-center">
                        <input type="number" min={0} step="0.01" value={l.box_price}
                          onChange={e => setLine(i, { box_price: e.target.value, priceEdited: true })}
                          className="w-28 h-9 text-center border border-slate-200 rounded-lg" />
                      </td>
                      <td className="p-2 text-center">
                        <input type="number" min={0} step="1" value={l.boxes}
                          onChange={e => setLine(i, { boxes: e.target.value })}
                          className="w-20 h-9 text-center border border-slate-200 rounded-lg" />
                      </td>
                      <td className="p-2 text-center">
                        <input type="number" min={0} step="1" value={l.pcs} disabled={order.mode !== "box_pcs"}
                          onChange={e => setLine(i, { pcs: e.target.value })}
                          className="w-20 h-9 text-center border border-slate-200 rounded-lg disabled:bg-slate-100" />
                      </td>
                      <td className="p-2 text-right font-semibold">{money(lineTotal(l))}</td>
                      <td className="p-2 text-center">
                        <button onClick={() => removeLine(i)} className="text-red-500 hover:bg-red-50 rounded px-2 py-1 text-xs" title="Remove item">Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <Button variant="outline" onClick={openBrowse}>+ Add Item (Browse Products)</Button>
              <div className="text-right">
                <div className="text-xs text-slate-500">Was {money(order.total)}</div>
                <div className="text-xl font-bold text-slate-900">Now {money(newTotal)}</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] text-white">{busy ? "Saving..." : "Save Changes"}</Button>
            </div>
          </div>
        </div>
      )}

      {open && browseOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="text-lg font-semibold text-slate-900">{cat && !q ? cat : "Browse Products"}</div>
              <div className="flex gap-2">
                {cat && !q && <Button variant="outline" onClick={() => setCat(null)}>All Categories</Button>}
                <Button variant="outline" onClick={() => setBrowseOpen(false)}>Close</Button>
              </div>
            </div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search product or SKU"
              className="w-full h-10 border border-slate-200 rounded-lg px-3 mb-3 text-sm" />

            {!cat && !q && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {catList.length === 0 && <div className="col-span-full text-center text-slate-400 p-6">No more products to add</div>}
                {catList.map(c => (
                  <button key={c.name} onClick={() => setCat(c.name)}
                    className="text-left bg-white border border-slate-200 hover:border-[#c9a227] hover:shadow-md transition rounded-2xl p-4 relative">
                    <div className="font-semibold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.count} product{c.count !== 1 ? "s" : ""}</div>
                    {c.picked > 0 && <span className="absolute top-3 right-3 bg-[#c9a227] text-white text-[10px] font-bold rounded-full px-2 py-0.5">{c.picked} selected</span>}
                  </button>
                ))}
              </div>
            )}

            {(cat || q) && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {visible.length === 0 && <div className="col-span-full text-center text-slate-400 p-6">No products found</div>}
                {visible.map(p => {
                  const id = pid(p);
                  const qv = qtyMap[id] || 0;
                  const price = Number(p.box_price ?? p.selling_price ?? 0);
                  return (
                    <div key={id} className="border border-slate-200 rounded-xl p-3 flex flex-col">
                      <div className="font-semibold text-slate-900 leading-tight">{p.name || p.product_name}</div>
                      <div className="text-xs font-mono text-slate-500 mb-1">{p.sku_code} - {p.box_qty || 1} pcs/box</div>
                      <div className="text-sm font-bold text-[#a67c00]">{price > 0 ? `${money(price)} / box` : "Price auto"}</div>
                      <div className="mt-3 flex items-center gap-1">
                        <button onClick={() => bump(id, -1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50">-</button>
                        <input type="number" min={0} value={qv} onChange={e => setQtyMap(prev => ({ ...prev, [id]: Math.max(0, Number(e.target.value) || 0) }))}
                          className="w-16 h-9 text-center border border-slate-200 rounded-lg text-sm font-semibold" />
                        <button onClick={() => bump(id, 1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 bg-[#faf6e6] text-[#a67c00]">+</button>
                        <span className="text-xs text-slate-400 ml-1">boxes</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setBrowseOpen(false)}>Cancel</Button>
              <Button onClick={addSelected} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] text-white">Add Selected to Order</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
